import React, { useEffect, useState } from 'react';
import Button from './Button.js';

const DocumentModal = ({
  isOpen,
  onClose,
  documentUrl,
  documentType,
  title = 'Document Attachment',
  zIndexClass = 'z-50',
}) => {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setImageError(false);
      setIsLoading(Boolean(documentUrl));
    }
  }, [isOpen, documentUrl]);

  if (!isOpen) return null;

  const url = (documentUrl || '').trim();
  const typeHint = String(documentType || '').toLowerCase();
  const isVideo =
    url.match(/\.(mp4|webm|mov|m4v|3gp)(\?|$)/i) ||
    typeHint.includes('video');
  const isImage =
    !isVideo &&
    (url.match(/\.(jpeg|jpg|png|gif|webp)(\?|$)/i) ||
      typeHint.includes('image') ||
      typeHint.includes('photo') ||
      typeHint.includes('receiver') ||
      (Boolean(url) && /^https?:\/\//i.test(url) && !url.match(/\.pdf(\?|$)/i)));
  const isPdf = url.match(/\.pdf(\?|$)/i) || typeHint === 'pdf';

  const handleImageLoad = () => {
    setIsLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 ${zIndexClass} flex items-center justify-center p-4`}
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {title}
            {documentType && documentType !== 'image' && documentType !== 'pdf' && (
              <span className="ml-2 text-sm text-gray-600">
                ({documentType.replace(/_/g, ' ').toUpperCase()})
              </span>
            )}
          </h3>
          <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close modal">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>

        <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
          {!url ? (
            <div className="text-center text-gray-500 py-12">
              <p className="text-lg font-medium mb-2">Document not available</p>
              <p className="text-sm">No file URL was provided.</p>
            </div>
          ) : imageError ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <p className="text-lg font-medium mb-2">Document not available</p>
              <p className="text-sm mb-4">The document could not be loaded.</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                Open in new tab
              </a>
            </div>
          ) : (
            <div className="relative min-h-[120px]">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded z-10">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
                </div>
              )}

              {isImage && (
                <img
                  src={url}
                  alt="Document"
                  className="w-full h-auto rounded shadow-lg"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}

              {isVideo && (
                <video
                  src={url}
                  controls
                  playsInline
                  className="w-full max-h-[70vh] rounded shadow-lg bg-black"
                  onLoadedData={() => setIsLoading(false)}
                  onError={handleImageError}
                />
              )}

              {isPdf && (
                <iframe
                  src={url}
                  title="PDF Document"
                  className="w-full h-[70vh] rounded"
                  onLoad={() => setIsLoading(false)}
                />
              )}

              {!isImage && !isVideo && !isPdf && (
                <div className="text-center text-gray-500 py-10">
                  <p className="mb-3">Preview not available for this file type.</p>
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    Open in new tab
                  </a>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
          {!imageError && url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              Open in New Tab
            </a>
          )}
          <Button variant="secondary" onClick={onClose} className="rounded-lg">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentModal;
